import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { DollarSign, FileText, Clock, FileCheck, Loader2 } from 'lucide-react';
import { api } from '../../api/client';
import PortalLayout from './PortalLayout';
// invoice_date / quote_date / due_date / payment_date are INSTANTS (api#838)
// and render as the TENANT's day via formatInstantTenantDay — their UTC
// prefix read a day late for anything written after 18:00 MDT, on the
// screen the CUSTOMER reads. valid_until is a calendar day and stays on
// formatCalendarDate.
//
// Before that they were treated as calendar days: `new Date()` read them as
// UTC midnight and this portal showed every one of them a day early. The local
// fmtDate that fixed that then ended in a bare toLocaleDateString(), so the
// FORMAT tracked the reader's own device instead: one customer saw 3/23/2026
// and another 23/03/2026 for the same day, with nothing on the page to say
// which. formatCalendarDate's default renders a month NAME, which no device
// can reorder into a different day.
import { formatInstantTenantDay } from '../../lib/calendar-date';
import { useTenantTimeZone } from '../../hooks/useTenantTimeZone';
import { fmtCurrency } from '../../lib/display-labels';

export default function PortalDashboard() {
  const timeZone = useTenantTimeZone();
  const navigate = useNavigate();
  const { data, isLoading } = useQuery({
    queryKey: ['portal-dashboard'],
    queryFn: () => api.portal.dashboard().then(r => r.data.data),
  });

  const stats = data?.stats;
  // Money on a document the CUSTOMER reads. `parseFloat(n || '0')` turned an
  // amount the server did not send into $0.00 — an invoice total that is
  // genuinely unknown is not an invoice for nothing. @see fmtCurrency.
  const fmt = (n: number | string | null | undefined) => fmtCurrency(n, 'CAD');
  return (
    <PortalLayout>
      {isLoading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 size={32} className="animate-spin text-fieldiq-accent" />
        </div>
      ) : (
        <div className="space-y-6 animate-fade-in">
          {/* Summary Cards */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <StatCard icon={<DollarSign size={20} />} label="Outstanding" value={fmt(stats?.outstandingBalance || 0)} color="text-amber-400" />
            <StatCard icon={<FileText size={20} />} label="Invoices Due" value={stats?.outstandingCount || 0} color="text-blue-400" />
            <StatCard icon={<Clock size={20} />} label="Overdue" value={stats?.overdueCount || 0} color="text-red-400" />
            <StatCard icon={<FileCheck size={20} />} label="Quotes Pending" value={stats?.pendingQuotes || 0} color="text-green-400" />
          </div>

          {/* Recent Invoices */}
          <div className="bg-fieldiq-card rounded-xl border border-fieldiq-border p-4">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-white font-semibold text-sm">Recent Invoices</h2>
              <button onClick={() => navigate('/portal/invoices')} className="text-xs text-fieldiq-accent hover:underline">View All</button>
            </div>
            {data?.recentInvoices?.length === 0 ? (
              <p className="text-fieldiq-muted text-sm py-4 text-center">No invoices yet</p>
            ) : (
              <div className="space-y-2">
                {data?.recentInvoices?.map((inv: any) => (
                  <button
                    key={inv.id}
                    onClick={() => navigate(`/portal/invoices/${inv.id}`)}
                    className="w-full flex items-center justify-between py-2.5 px-3 bg-fieldiq-dark rounded-lg hover:bg-fieldiq-dark/80 transition-colors"
                  >
                    <div className="text-left">
                      <p className="text-white text-sm font-medium">{inv.invoice_number}</p>
                      <p className="text-fieldiq-muted text-xs">{(formatInstantTenantDay(inv.invoice_date, timeZone) ?? '—')}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-white text-sm font-medium">{fmt(parseFloat(inv.balance_due || '0'))}</p>
                      <StatusBadge status={inv.status} />
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Recent Quotes */}
          <div className="bg-fieldiq-card rounded-xl border border-fieldiq-border p-4">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-white font-semibold text-sm">Pending Quotes</h2>
              <button onClick={() => navigate('/portal/quotes')} className="text-xs text-fieldiq-accent hover:underline">View All</button>
            </div>
            {data?.recentQuotes?.length === 0 ? (
              <p className="text-fieldiq-muted text-sm py-4 text-center">No pending quotes</p>
            ) : (
              <div className="space-y-2">
                {data?.recentQuotes?.map((q: any) => (
                  <button
                    key={q.id}
                    onClick={() => navigate(`/portal/quotes/${q.id}`)}
                    className="w-full flex items-center justify-between py-2.5 px-3 bg-fieldiq-dark rounded-lg hover:bg-fieldiq-dark/80 transition-colors"
                  >
                    <div className="text-left">
                      <p className="text-white text-sm font-medium">{q.quote_number}</p>
                      <p className="text-fieldiq-muted text-xs">{(formatInstantTenantDay(q.quote_date, timeZone) ?? '—')}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-white text-sm font-medium">{fmt(parseFloat(q.total_amount || '0'))}</p>
                      <StatusBadge status={q.status} />
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </PortalLayout>
  );
}

function StatCard({ icon, label, value, color }: { icon: React.ReactNode; label: string; value: string | number; color: string }) {
  return (
    <div className="bg-fieldiq-card rounded-xl border border-fieldiq-border p-3">
      <div className={`${color} mb-1`}>{icon}</div>
      <p className="text-white font-bold text-lg">{value}</p>
      <p className="text-fieldiq-muted text-xs">{label}</p>
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const styles: Record<string, string> = {
    sent: 'bg-blue-500/20 text-blue-400',
    paid: 'bg-green-500/20 text-green-400',
    overdue: 'bg-red-500/20 text-red-400',
    accepted: 'bg-green-500/20 text-green-400',
    declined: 'bg-red-500/20 text-red-400',
  };
  return (
    <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${styles[status] || 'bg-gray-500/20 text-gray-400'}`}>
      {status?.toUpperCase()}
    </span>
  );
}
